import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const SUBS = ["oppen", "hybrid", "intern"];

export default function AdminOrganizations({ api, t }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // org being edited
  const [form, setForm] = useState({ name: "", subscription: "oppen", owner_email: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/organizations");
      setOrgs(data || []);
    } catch {
      toast.error("Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ name: "", subscription: "oppen", owner_email: "" });
    setCreating(false);
    setEditing(null);
  };

  const startEdit = (o) => {
    setEditing(o.id);
    setCreating(false);
    setForm({ name: o.name || "", subscription: o.subscription || "oppen", owner_email: o.owner_email || "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      if (editing) {
        await api.patch(`/admin/organizations/${editing}`, form);
        toast.success(t("admin.orgs.updateOk"));
      } else {
        await api.post("/admin/organizations", form);
        toast.success(t("admin.orgs.createOk"));
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm(t("admin.orgs.confirmDelete"))) return;
    try {
      await api.delete(`/admin/organizations/${id}`);
      toast.success(t("admin.orgs.deleteOk"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const showForm = creating || editing;

  return (
    <div data-testid="admin-orgs-section">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl">{t("admin.orgs.title")}</h2>
        {!showForm && (
          <button className="btn-primary" onClick={() => { setCreating(true); setEditing(null); }} data-testid="admin-org-new-btn">
            <Plus size={14} /> {t("admin.orgs.newBtn")}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-5 border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-org-form">
          <div>
            <div className="label-tech mb-2">{t("admin.orgs.name")}</div>
            <input className="input-tech" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-org-name-input" />
          </div>
          <div>
            <div className="label-tech mb-2">{t("admin.orgs.type")}</div>
            <select className="input-tech" value={form.subscription} onChange={(e) => setForm({ ...form, subscription: e.target.value })} data-testid="admin-org-type-select">
              {SUBS.map((s) => <option key={s} value={s}>{t(`org.subShort.${s}`)}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tech mb-2">{t("admin.orgs.owner")}</div>
            <input className="input-tech" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="name@company.se" data-testid="admin-org-owner-input" />
          </div>
          <div className="md:col-span-3 flex gap-2 justify-end">
            <button type="button" className="btn-ghost" onClick={resetForm} data-testid="admin-org-cancel-btn">{t("admin.orgs.cancel")}</button>
            <button type="submit" className="btn-primary" data-testid="admin-org-submit-btn">{t("admin.orgs.save")}</button>
          </div>
        </form>
      )}

      <div className="mt-6 border border-[var(--ink-200)] bg-[var(--bone)]">
        {loading ? (
          <div className="p-5 label-tech">…</div>
        ) : orgs.length === 0 ? (
          <div className="p-5 label-tech">{t("admin.orgs.empty")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--ink-200)] label-tech">
                <th className="text-left p-3">{t("admin.orgs.name")}</th>
                <th className="text-left p-3">{t("admin.orgs.type")}</th>
                <th className="text-left p-3">{t("admin.orgs.owner")}</th>
                <th className="text-right p-3">{t("admin.orgs.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id} className="border-b border-[var(--ink-200)]" data-testid={`admin-org-row-${o.id}`}>
                  <td className="p-3 font-display">{o.name}</td>
                  <td className="p-3"><span className="tag tag-moss">{t(`org.subShort.${o.subscription}`) || o.subscription}</span></td>
                  <td className="p-3 text-[var(--ink-700)]">{o.owner_email || "—"}</td>
                  <td className="p-3 text-right">
                    <button className="btn-ghost inline-flex items-center gap-1 mr-2" onClick={() => startEdit(o)} data-testid={`admin-org-edit-${o.id}`}>
                      <Pencil size={12} /> {t("admin.orgs.edit")}
                    </button>
                    <button className="btn-ghost inline-flex items-center gap-1 text-[var(--brick)]" onClick={() => del(o.id)} data-testid={`admin-org-delete-${o.id}`}>
                      <Trash2 size={12} /> {t("admin.orgs.delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
